import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { adminService } from '../../../services/adminService';
import { extractList, extractMeta } from '../../../services/api';
import { Loading, EmptyState } from '../../../shared/components';
import { ROLES } from '../../../constants';
import './AdminCommonStyles.scss';

const StatusBadge = ({ value }) => (
  <span className={`status-badge status-badge--${value === 'active' ? 'active' : 'inactive'}`}>
    {value === 'active' ? 'Aktif' : 'Nonaktif'}
  </span>
);

const UniformCategoriesPage = () => {
  const { user } = useAuth();
  const isSuperadmin = user?.role === ROLES.SUPERADMIN;
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    status: 'active',
    company_id: '',
  });

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    fetchCategories();
    if (isSuperadmin) {
      adminService.getCompanies({ limit: 100 })
        .then((res) => setCompanies(extractList(res.data)))
        .catch(() => showToast('Gagal memuat daftar perusahaan'));
    }
  }, [page]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (user?.role === 'superadmin') {
        // Superadmin bisa filter by company
      } else {
        // Admin company hanya lihat category milik company sendiri
      }
      const res = await adminService.getUniformCategories(params);
      setCategories(extractList(res.data));
      setPageInfo(extractMeta(res.data));
    } catch (err) {
      showToast(err?.message || 'Gagal memuat kategori seragam');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setFormData({
      name: '', code: '', description: '', status: 'active',
      company_id: isSuperadmin ? '' : (user?.company_id || ''),
    });
    setShowForm(true);
  };

  const handleEdit = async (id) => {
    const res = await adminService.getUniformCategoryById(id);
    if (res.status) {
      setFormData(res.data);
      setEditingId(id);
      setShowForm(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      showToast('Nama dan kode harus diisi');
      return;
    }
    if (isSuperadmin && !editingId && !formData.company_id) {
      showToast('Pilih perusahaan terlebih dahulu');
      return;
    }
    try {
      const payload = { ...formData };
      if (payload.company_id) payload.company_id = Number(payload.company_id);
      if (!isSuperadmin) delete payload.company_id;
      const res = editingId
        ? await adminService.updateUniformCategory(editingId, payload)
        : await adminService.createUniformCategory(payload);
      if (res.status) {
        showToast(res.message || 'Kategori berhasil disimpan');
        fetchCategories();
        setShowForm(false);
      } else {
        showToast(res.message || 'Gagal menyimpan kategori');
      }
    } catch (err) {
      showToast(err?.message || 'Gagal menyimpan kategori');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Hapus kategori ini? Data uniform yang terkait akan tetap ada.')) {
      try {
        const res = await adminService.deleteUniformCategory(id);
        if (res.status) {
          showToast(res.message || 'Kategori berhasil dihapus');
          fetchCategories();
        } else {
          showToast(res.message || 'Gagal menghapus kategori');
        }
      } catch (err) {
        showToast(err?.message || 'Gagal menghapus kategori');
      }
    }
  };

  const filtered = categories.filter((cat) => {
    if (filterStatus && cat.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!cat.name.toLowerCase().includes(q) && !cat.code.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (loading) return <Loading />;

  return (
    <div className="page-container page-container--full">
      {toast && <div className="toast">{toast}</div>}
      <div className="page-header">
        <h1>Kategori Seragam</h1>
        <p>Kelola kategori seragam di perusahaan</p>
      </div>

      <div className="page-controls" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
        <div className="search-icon-wrap">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Cari nama atau kode..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="">Semua Status</option>
          <option value="active">Aktif</option>
          <option value="inactive">Nonaktif</option>
        </select>
        <button className="btn-primary" onClick={handleAdd} style={{ boxShadow: '0 4px 12px rgba(18,183,173,0.3)', whiteSpace: 'nowrap' }}>+ Tambah Kategori</button>
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="Tidak ada kategori seragam" />
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Kode</th>
                {isSuperadmin && <th>Perusahaan</th>}
                <th>Deskripsi</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((cat) => (
                <tr key={cat.uniform_category_id}>
                  <td className="category-name-cell"><strong>{cat.name}</strong></td>
                  <td><span className="uniform-code">{cat.code}</span></td>
                  {isSuperadmin && (
                    <td className="text-muted">{cat.company?.name || '—'}</td>
                  )}
                  <td
                    className="text-muted desc-truncate"
                    title={cat.description || ''}
                  >{cat.description || '—'}</td>
                  <td><StatusBadge value={cat.status} /></td>
                  <td>
                    <button className="btn-sm btn-info" onClick={() => handleEdit(cat.uniform_category_id)}>Edit</button>
                    <button className="btn-sm btn-danger" onClick={() => handleDelete(cat.uniform_category_id)}>Hapus</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pageInfo.total > 10 && (
        <div className="pagination">
          <button onClick={() => setPage(page - 1)} disabled={page === 1}>← Previous</button>
          <span>Page {page} of {pageInfo.last_page}</span>
          <button onClick={() => setPage(page + 1)} disabled={page === pageInfo.last_page}>Next →</button>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingId ? 'Edit Kategori' : 'Tambah Kategori Baru'}</h2>
            <form onSubmit={handleSubmit}>
              {isSuperadmin && !editingId && (
                <div className="form-group">
                  <label>Perusahaan *</label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    required
                  >
                    <option value="">Pilih perusahaan</option>
                    {companies.map((c) => (
                      <option key={c.company_id} value={c.company_id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {isSuperadmin && editingId && (
                <div className="form-group">
                  <label>Perusahaan</label>
                  <input
                    type="text"
                    value={companies.find((c) => String(c.company_id) === String(formData.company_id))?.name || formData.company?.name || '—'}
                    disabled
                    style={{ background: '#f5f5f5', color: '#888', cursor: 'not-allowed' }}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Nama Kategori *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Kode *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Deskripsi</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">Simpan</button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UniformCategoriesPage;
